import Box from "@mui/material/Box";
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton } from "@mui/x-data-grid";
import { useOktaAuth } from "@okta/okta-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCOOSurveyPartsBySupplier } from "../../services/coo.service";
import { COO_RESPONSE_STATUSES, COO_TOOLTIP_DUAL_SOURCE, COO_COUNTRY_OVERRIDE_CONFIRMATION } from "../../utils/surveyLiterals";
import { CircleProgressIndicator } from "../CircleProgressIndicator";
import { getCountry } from "../../services/external.service";
import { ReactComponent as AddIcon } from "@deere/ux.brand-foundations/icons/add_circle.svg";
import { IconButton, Typography, FormControl, FormLabel, SelectField, Option, Tooltip } from "@deere/fuel-react";
import ModalAddCountry from "./ModalAddCountry";
import { ModalGeneric } from "../ModalGeneric";

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

export const PartsTable = ({ campaign, suppliers, updatePartsArray, partsInResponse, selectedResponseId, selectionModel, setSelectionModel, editedPartCountries, setEditedPartCountries }) => {
  const [data, setData] = useState(undefined);
  const [countryList, setCountryList] = useState([]);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [rowToUpdateCountry, setRowToUpdateCountry] = useState(null);
  const [rowsToDisable, setRowsToDisable] = useState([]);
  const [countryDropDownValue, setCountryDropDownValue] = useState("");
  const [showModal, setShowModal] = useState(false);
  const { authState } = useOktaAuth();
  const { t } = useTranslation();
  const columns = useMemo(() => [
    { field: "supplierNumber", headerName: t("parts_tbl_column_header_1"), width: 120 },
    { field: "deerePartNumber", headerName: t("parts_tbl_column_header_2"), width: 120 },
    { field: "deerePartDesc", headerName: t("parts_tbl_column_header_3"), width: 180 },
    { field: "unitCd", headerName: t("parts_tbl_column_header_10"), width: 100 },
    { field: "strCountry", headerName: t("parts_tbl_column_header_6"), width: 200, renderCell: (params) => renderCountryCell(params) },
    { field: "status", headerName: t("parts_tbl_column_header_5"), width: 100 },
  ]);
  const style = {
    width: "100%",
    "& .disable-row-status": { color: "#b3b3b3" },
  };

  useEffect(() => {
    if (selectedResponseId && selectedResponseId !== "" && partsInResponse && partsInResponse.length > 0) {
      let tempArr = [];
      partsInResponse.forEach((part) => {
        let index = tempArr.findIndex((d) => d.id === part.id);
        if (index === -1) {
          tempArr.push({
            id: part.id,
            country: [part.country],
            supplierNumber: part.supplierNumber,
            deerePartNumber: part.deerePartNumber,
            deerePartDesc: part.deerePartDesc,
            unitCd: part.unitCd,
          });
        } else tempArr[index].country = [...tempArr[index].country, part.country];
      });
      setEditedPartCountries(tempArr);
    }
  }, [selectedResponseId]);

  useEffect(() => {
    getCountry(authState.accessToken.accessToken)
      .then((res) => {
        if (res.length > 0) {
          res.sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0));
          setCountryList(res);
        }
      })
      .catch((err) => {
        console.log(err);
        setCountryList([]);
      });
  }, []);

  useEffect(() => {
    if (Array.isArray(suppliers) && suppliers.length > 0) {
      getCOOSurveyPartsBySupplier(suppliers, campaign.id, authState.accessToken.accessToken)
        .then((response) => {
          if (Array.isArray(response)) {
            response.sort((a, b) => (a.id > b.id ? 1 : a.id < b.id ? -1 : 0));
            let tempArr = [];
            let tempArrDisable = [];
            const responseActive = [], responseDisabled = [];
            response.forEach((res) => {
              let editedPartCountryFound = editedPartCountries.find((d) => d.id === res.id);
              if (editedPartCountryFound) {
                res.country = editedPartCountryFound.country; //Retaining the country change done by user via popup/drop-down
                res.strCountry = editedPartCountryFound.country.join(", "); //Adding this property for display purpose
              } else {
                res.strCountry = res.country.join(", "); //Adding this property for display purpose
              }
              //**Logic explanation at bottom
              if (res.status.trim().toUpperCase() === COO_RESPONSE_STATUSES.SUBMITTED.toUpperCase() || res.status.trim().toUpperCase() === COO_RESPONSE_STATUSES.IN_PROCESS.toUpperCase()){
                if (selectedResponseId && selectedResponseId !== "") {
                  let index = partsInResponse.findIndex((part) => part.id === res.id);
                  if (index === -1){ 
                    tempArrDisable.push(res.id);
                    responseDisabled.push(res);
                  }
                  else {
                    tempArr.push({
                      id: res.id,
                      country: res.country,
                      supplierNumber: res.supplierNumber,
                      deerePartNumber: res.deerePartNumber,
                      deerePartDesc: res.deerePartDesc,
                      unitCd: res.unitCd
                    });
                    responseActive.push(res);
                  }
                } else {
                  tempArrDisable.push(res.id);
                  responseDisabled.push(res);
                }
              }else{
                responseActive.push(res);
              }
            });
            setRowsToDisable(tempArrDisable);
            setData([...responseActive, ...responseDisabled]); // This will sort the disabled parts at the bottom & keep active ones at the top.
            updatePartsArray(tempArr);
          }
        })
        .catch((error) => {
          throw error;
        });
    } else {
      setData(undefined);
    }
  }, [authState.accessToken.accessToken, campaign.id, suppliers]);

  const countryChangeDropdownHandler = (value, isConfirmationReceived) => {
    setCountryDropDownValue(value);
    let isConfirmationNeeded = false;
    if(!isConfirmationReceived){
      for (let i = 0; i < selectionModel.length; i++) {
        let selectedData = data.find((d) => d.id === selectionModel[i]);
        if (selectedData.country && selectedData.country.length > 0) {
          isConfirmationNeeded = true;
          break;
        }
      }
    }
    if(isConfirmationNeeded)  setShowModal(true);
    else{
      setData((prevState) => {
        prevState.forEach((d) => {
          let index = selectionModel.findIndex((sel) => sel === d.id);
          if (index > -1) d.country = d.strCountry = [value];
        });
        return [...prevState];
      });
      let tempArr = [...editedPartCountries];
      selectionModel.forEach((sel) => {
        let index = tempArr.findIndex((d) => d.id === sel);
        const partData=data.filter(i=>i.id===sel)[0];
        index > -1 ? (tempArr[index].country = [value]) :
         tempArr.push({
          id: sel,
          country: [value],
          supplierNumber:partData.supplierNumber,
          deerePartNumber:partData.deerePartNumber,
          deerePartDesc:partData.deerePartDesc,
          unitCd: partData.unitCd,
         });
      });
      setEditedPartCountries(tempArr);
      updatePartsArray(tempArr);
      setShowModal(false);
      setCountryDropDownValue("");
    }
  };

  const countryChangePopupHandler = (id, arrCountries) => {
    setData((prevState) => {
      prevState.forEach((d) => {
        if (d.id === id) {
          d.country = arrCountries;
          d.strCountry = arrCountries.join(", ");
        }
      });
      return [...prevState];
    });
    let tempArr = [...editedPartCountries];
    let found = false;
    tempArr.forEach((d) => {
      if (d.id === id) {
        found = true;
        d.country = arrCountries;
      }
    });
    if (!found) {
      const partData = data.filter((i) => i.id === id)[0];
      tempArr.push({
        id: id,
        country: arrCountries,
        supplierNumber: partData.supplierNumber,
        deerePartNumber: partData.deerePartNumber,
        deerePartDesc: partData.deerePartDesc,
        unitCd: partData.unitCd,
      });
    }
    setEditedPartCountries(tempArr);
    updatePartsArray(tempArr);
  };

  const disableRow = (id) => {
    let index = rowsToDisable.findIndex((rowId) => rowId === id);
    if (index === -1) return true;
    return false;
  };

  const setDisableRowStyle = (id) => {
    let index = rowsToDisable.findIndex((rowId) => rowId === id);
    if (index > -1) return "disable-row-status";
    return "";
  };

  const renderCountryCell = (params) => {
    const checkDisable = (id) => {
      let index = rowsToDisable.findIndex((rowId) => rowId === id);
      if (index > -1) return true;
      else return false;
    };
    return (
      <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
        <span>{params.row.strCountry}</span>
        <Tooltip title={COO_TOOLTIP_DUAL_SOURCE} placement="top">
          <IconButton
            onClick={() => {
              setRowToUpdateCountry(params.row);
              setShowCountryModal(true);
            }}
            disabled={checkDisable(params.row.id)}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      </div>
    );
  };

  const dTable = (
    <Box sx={style}>
      <DataGrid
        rows={data}
        columns={columns}
        autoHeight
        pageSize={5}
        checkboxSelection
        disableSelectionOnClick
        disableColumnMenu
        isRowSelectable={(params) => disableRow(params.row.id)}
        getRowClassName={(params) => setDisableRowStyle(params.row.id)}
        onSelectionModelChange={(ids) => setSelectionModel(ids)}
        components={{ Toolbar: CustomToolbar }}
        selectionModel={selectionModel}
        sx={{ "& .MuiDataGrid-cell": { whiteSpace: "normal!important" } }}
        getRowHeight={() => "auto"}
      />
    </Box>
  );

  return Array.isArray(data) ? (
    <>
      <Typography size="md" component="p" gutterBottom fontWeight="semibold" paddingLeft={2} marginTop={2}>
        {t("parts_section_lbl_1")}
      </Typography>
      <Typography size="sm" component="p" gutterBottom fontWeight="regular" paddingLeft={2}>
        {t("parts_section_sub_lbl_1")}
      </Typography>
      {dTable}
      <FormControl variant="standard" sx={{ paddingLeft: "8px" }}>
        <FormLabel>
          <Typography size="md" component="p" gutterBottom fontWeight="semibold" marginTop={2}>
            {t("parts_section_lbl_2")}
          </Typography>
        </FormLabel>
        <SelectField
          defaultValue=""
          disabled={selectionModel.length === 0}
          onChange={(e) => {
            countryChangeDropdownHandler(e.target.value, false);
          }}
          value={countryDropDownValue}
        >
          <Option disabled hidden value="">
            {t("lbl_dropdown_select")}
          </Option>
          {countryList.map((opt) => (
            <Option key={opt.iso2CountryCode} value={opt.name}>
              {opt.name}
            </Option>
          ))}
        </SelectField>
      </FormControl>
      {showCountryModal && (
        <ModalAddCountry showModal={showCountryModal} setShowModal={setShowCountryModal} rowToUpdateCountry={rowToUpdateCountry} countryList={countryList} saveHandler={countryChangePopupHandler} />
      )}
      <ModalGeneric
        showModal={showModal}
        setShowModal={setShowModal}
        labelBtnPrimary={t("confirm_label")}
        labelBtnSecondary={t("btn_cancel")}
        msgModal={COO_COUNTRY_OVERRIDE_CONFIRMATION}
        titleModal={t("confirm_label")}
        onClose={() => {
          setShowModal(false);
          setCountryDropDownValue("");
        }}
        handleSubmit={()=>{countryChangeDropdownHandler(countryDropDownValue, true)}}
      />
    </>
  ) : (
    <CircleProgressIndicator title={t("tps_lbl_waiting_country_message_title")} subtitle={t("tps_lbl_waiting_country_message_subtitle")} />
  );
};

//**
// Logic for Row Disable-
// if the Status is SUBMITTED or IN PROCESS
//    - check if selectedResponseId is available
//      - If available that means a response is selected & now we can check if the part is present in the 'partsInResponse' array.
//        - If present, that means this part is tied to the response currently selected, so it can still be edited.
//        - If not present, that means this part is tied to a different response, so cannot be edited here.
//      - If not available meaning a response is not selected and a new response is going to be created,
//        so all the IN_PROCESS/SUBMITTED parts should not be allowed to be edited since they all are tied to some different responses.
